import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type {
	ConnectionsApiError,
	ConnectionsTableProps as ConnectionsTableEntryPointProps,
} from '@databox/ember-integrations/lib/components/Connections/ConnectionsTable';
import ConnectionsTableEntryPoint from '@databox/ember-integrations/lib/components/Connections/ConnectionsTableEntryPoint';
import type { HostDataSourceType } from '@databox/ember-integrations/lib/types/host.types';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AccountType } from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getShadowDomMode, isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonAddButtonComponent from 'webapp/components/common/add-button';

import type FlowIntegrationsSelectionPopup from 'webapp/services/flow/integrations-selection-popup';

type Args = Record<string, never>;

/**
 * The Connections page: the React connections table from `@databox/connect`, mounted the same way as the
 * Datasets and Merged datasets pages. Adding a connection and asking a teammate run the webapp's own flows
 * (integrations library, Connect), and the table's results surface as infolayer messages.
 */
export default class DataManagerConnectionsComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('react') declare reactService: Service.React;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('connect') declare connectService: Service.Connect;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('flow/integrations-selection-popup')
	declare flowIntegrationsSelectionPopup: FlowIntegrationsSelectionPopup;

	node: HTMLDivElement | null = null;
	reactElement: ReactIntegrations.Root = null;

	@tracked isEmptyState = false;

	addButtonText = 'Add connection';

	get addButtonContainerNode() {
		return isAlive(this) ? this.navigationService.addButtonContainerNode : null;
	}

	// Same as the Data sources page's "Add data source": the webapp's own integrations library.
	@action
	addConnection(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.flowIntegrationsSelectionPopup.isEligibleToCheckToOpenPopup = false;
		this.navigationService.transitionToRoute('datamanager.available');
		this.analyticsService.event(TrackingEvent.NEW_CONNECTION, {
			origin: 'connections',
		});
	}

	@action
	askTeammateToConnect(sourceType: HostDataSourceType): void {
		this.connectService.helpConnectSource(sourceType);
	}

	@action
	handleConnectionDeleted(): void {
		this.infolayerService.ok('Connection deleted successfully');
	}

	@action
	handleConnectionUpdated(): void {
		this.datasetEventsService.handleOnConnectionUpdated();
	}

	@action
	handleApiError(error: ConnectionsApiError): void {
		this.datasetEventsService.handleApiError(error as ReactIntegrations.Error);
	}

	// A connect or reauthenticate from a row failed; same copy as the React toast, without the raw reason.
	@action
	handleConnectFailed(): void {
		this.infolayerService.error('Error while connecting new source, please try again.');
	}

	// The React empty state has its own Add connection; the header button would duplicate it.
	@action
	setEmptyState(empty: boolean) {
		this.isEmptyState = empty;
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
		this.reactElement = this.reactService.create(this.node);

		this.renderConnections();
		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderConnections);
	}

	renderConnections() {
		const { accessToken, userDataToken: dataToken } = this.currentUserService;

		const props: ConnectionsTableEntryPointProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			accessToken: accessToken!,
			dataToken: dataToken!,
			spaceId: this.accountService.id,
			isWhiteLabeled: this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins,
			replaceHelpUrls: this.accountService.replaceHelpUrl,
			isAccountTypeAgency: this.accountService.accountType === AccountType.AGENCY,
			isAccountTypeClient: this.accountService.accountType === AccountType.CLIENT,
			onEmptyTable: this.setEmptyState,
			onAddConnection: this.addConnection,
			onAskTeammateToConnect: this.askTeammateToConnect,
			onConnectionDeleted: this.handleConnectionDeleted,
			onConnectionUpdated: this.handleConnectionUpdated,
			onApiError: this.handleApiError,
			onConnectFailed: this.handleConnectFailed,
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				ConnectionsTableEntryPoint(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);

		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			id="connections-table-container"
		/>

		{{#if this.addButtonContainerNode}}
			{{#unless this.isEmptyState}}
				{{#in-element this.addButtonContainerNode}}
					<CommonAddButtonComponent
						@text={{this.addButtonText}}
						@callTrigger={{this.addConnection}}
					/>
				{{/in-element}}
			{{/unless}}
		{{/if}}
	</template>
}
